// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Layout Wrapper
// THE FIX for sidebar freeze: Layout owns the activeView state, sidebar is passive
// Main content re-renders don't cause sidebar re-renders (memo + stable callbacks)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, memo } from 'react';
import { Crown } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { Button } from '@components/ui/Primitives';

// ─── Admin impersonation banner ──────────────────────────────────────────
const ImpersonationBanner = memo(function ImpersonationBanner({ profile, onExit }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();

  return (
    <div style={{
      background: `linear-gradient(90deg, ${theme.warning}20, ${theme.warning}08)`,
      borderBottom: `1px solid ${theme.warning}30`,
      padding: '8px 28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        fontWeight: 600,
        color: theme.warning
      }}>
        <Crown size={14} />
        {t.adminOverrideBar} {lang === 'ar' ? profile?.full_name_ar : profile?.full_name_en}
      </div>
      <Button variant="ghost" size="sm" onClick={onExit}>
        {t.exitOverride}
      </Button>
    </div>
  );
});

// ─── Main Layout ─────────────────────────────────────────────────────────
export function Layout({
  activeView,
  setActiveView,
  children,
  features = {},
  isAdminView,
  onToggleAdminView
}) {
  const { theme } = useTheme();
  const { dir } = useLang();
  const { isImpersonating, impersonating, exitImpersonation } = useAuth();

  // Stable navigation callback — THIS is the fix
  // Sidebar only re-renders when activeView actually changes
  const handleNavigate = useCallback((view) => {
    setActiveView(view);
  }, [setActiveView]);

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: theme.bg,
      direction: dir,
      color: theme.text
    }}>
      <Sidebar
        activeView={activeView}
        onNavigate={handleNavigate}
        features={features}
        isAdminView={isAdminView}
        onToggleAdminView={onToggleAdminView}
      />

      <main style={{
        flex: 1,
        minWidth: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {isImpersonating && (
          <ImpersonationBanner
            profile={impersonating}
            onExit={exitImpersonation}
          />
        )}
        <div style={{ padding: '28px 28px 40px', flex: 1 }}>
          {children}
        </div>
      </main>
    </div>
  );
}
